import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ChevronDown, Plus, Check } from "lucide-react";
import { useFamily } from "../../contexts/FamilyContext";

const RELATION_LABELS = {
    self: "Me",
    father: "Father",
    mother: "Mother",
    spouse: "Spouse",
    son: "Son",
    daughter: "Daughter",
    brother: "Brother",
    sister: "Sister",
    other: "Other",
};

const initials = (name) => {
    if (!name) return "·";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || name[0].toUpperCase();
};

const Avatar = ({ name, photoUrl, size = "sm" }) => {
    const sz = size === "lg" ? "h-9 w-9 text-sm" : "h-7 w-7 text-[11px]";
    if (photoUrl) {
        return <img src={photoUrl} alt={name} className={`${sz} rounded-full object-cover ring-2 ring-white dark:ring-zinc-900`} />;
    }
    return (
        <span className={`${sz} inline-flex items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-500 font-semibold text-white ring-2 ring-white dark:ring-zinc-900`}>
            {initials(name)}
        </span>
    );
};

const FamilySwitcher = () => {
    const { members, activeMember, setActiveId } = useFamily();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    if (!members.length) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                data-testid="family-switcher-trigger"
                title="Switch family member"
                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-2 py-1 text-xs font-medium text-zinc-700 transition hover:border-brand-300 hover:text-brand-700 dark:border-white/10 dark:bg-zinc-900/60 dark:text-zinc-200 dark:hover:border-brand-400/40"
            >
                <Avatar name={activeMember?.full_name} photoUrl={activeMember?.photo_url} />
                <span className="hidden max-w-[100px] truncate sm:inline-block">
                    {activeMember?.is_self ? "Me" : activeMember?.full_name || "—"}
                </span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </button>
            {open && (
                <div
                    data-testid="family-switcher-menu"
                    className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-xl dark:border-white/10 dark:bg-zinc-900"
                >
                    <div className="border-b border-zinc-100 px-4 py-2.5 dark:border-white/10">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Profile</p>
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Currently managing</p>
                    </div>
                    <ul className="max-h-72 overflow-y-auto py-1">
                        {members.map((m) => {
                            const isActive = m.id === activeMember?.id;
                            return (
                                <li key={m.id}>
                                    <button
                                        onClick={() => { setActiveId(m.id); setOpen(false); }}
                                        data-testid={`family-switcher-option-${m.id}`}
                                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                                            isActive
                                                ? "bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                                : "text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-white/5"
                                        }`}
                                    >
                                        <Avatar name={m.full_name} photoUrl={m.photo_url} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-medium">{m.is_self ? "Me" : m.full_name}</p>
                                            <p className="truncate text-[11px] text-zinc-500 dark:text-zinc-400">
                                                {RELATION_LABELS[m.relation] || m.relation}
                                                {m.dob ? ` · ${m.dob}` : ""}
                                            </p>
                                        </div>
                                        {isActive && <Check className="h-4 w-4 shrink-0 text-brand-700 dark:text-brand-300" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <Link
                        to="/family"
                        onClick={() => setOpen(false)}
                        data-testid="family-switcher-manage-link"
                        className="flex items-center justify-between gap-2 border-t border-zinc-100 px-4 py-2.5 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:border-white/10 dark:text-brand-300 dark:hover:bg-brand-500/10"
                    >
                        <span className="inline-flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Manage family</span>
                        <Plus className="h-3.5 w-3.5" />
                    </Link>
                </div>
            )}
        </div>
    );
};

export default FamilySwitcher;
